/**
 * Test Personalitate. Mini-IPIP, 20 items, four per Big Five factor.
 *
 * Donnellan, M. B., Oswald, F. L., Baird, B. M., & Lucas, R. E. (2006).
 * The Mini-IPIP scales: tiny-yet-effective measures of the Big Five factors
 * of personality. Psychological Assessment, 18(2), 192–203.
 * DOI 10.1037/1040-3590.18.2.192
 *
 * Items and keying checked against the scoring key on ipip.ori.org
 * (https://ipip.ori.org/MiniIPIPKey.htm), 2026-09-13. Licence, verbatim from
 * ipip.ori.org: "The items and scales are in the public domain, which means
 * that one can copy, edit, translate, or use them for any purpose without
 * asking permission and without paying a fee."
 *
 * Why this is not a `TestConfig`: that contract is built around one score,
 * a set of bands and per-band copy, email styles and a therapist CTA. The
 * Mini-IPIP returns five independent dimension scores and has no bands, no
 * cut-offs and nothing to escalate, so forcing it into `TestConfig` would
 * mean inventing categories the instrument does not have. It gets its own
 * small contract instead, and `TestPageLayout` accepts the structural subset
 * both contracts share.
 *
 * Romanian wording is a Mindtale translation, not a validated adaptation.
 */

export type BigFiveFactor = "E" | "A" | "C" | "N" | "I";

export type FactorScores = Record<BigFiveFactor, number>;

export interface ProfileItem {
  id: number;
  text: string;
  factor: BigFiveFactor;
  /** +1 scores the answer as given, -1 reverses it (6 - value). */
  keyed: 1 | -1;
}

export interface FactorCopy {
  key: BigFiveFactor;
  /** Romanian name shown in the result. */
  name: string;
  /** Name used in the Mini-IPIP paper, for readers who look it up. */
  original: string;
  /** What the dimension describes, in one or two sentences. */
  what: string;
  low: { label: string; text: string };
  high: { label: string; text: string };
}

export interface ProfileTestConfig {
  slug: "personalitate";
  questions: ProfileItem[];
  options: { label: string; value: number }[];
  allowedAnswerValues: number[];
  factors: FactorCopy[];
  /** Lowest and highest possible sum for one factor (4 items × 1..5). */
  factorMin: number;
  factorMax: number;
  score: (answers: number[]) => FactorScores;
  totalQuestions: number;
  estimatedMinutes: number;
  stepperFootnote: string;
  source: { name: string };
  meta: {
    title: string;
    description: string;
    keywords: string[];
    canonicalUrl: string;
  };
}

/* Interleaved E, A, C, N, I so no two items from the same factor sit next to
   each other, following the administration order in Donnellan et al. (2006)
   as recalled; the ipip.ori.org key lists items by factor, not by order, so
   check the order against the paper's appendix. Factor and keying of every
   item were checked against that key. */
const QUESTIONS: ProfileItem[] = [
  { id: 1, factor: "E", keyed: 1, text: "Sunt sufletul petrecerii." },
  { id: 2, factor: "A", keyed: 1, text: "Empatizez cu sentimentele celorlalți." },
  { id: 3, factor: "C", keyed: 1, text: "Îmi fac treburile imediat, fără să le amân." },
  { id: 4, factor: "N", keyed: 1, text: "Am des schimbări de dispoziție." },
  { id: 5, factor: "I", keyed: 1, text: "Am o imaginație bogată." },
  { id: 6, factor: "E", keyed: -1, text: "Nu vorbesc mult." },
  { id: 7, factor: "A", keyed: -1, text: "Nu mă interesează problemele altora." },
  { id: 8, factor: "C", keyed: -1, text: "Uit des să pun lucrurile înapoi la locul lor." },
  { id: 9, factor: "N", keyed: -1, text: "Sunt relaxat/ă aproape tot timpul." },
  { id: 10, factor: "I", keyed: -1, text: "Nu mă interesează ideile abstracte." },
  { id: 11, factor: "E", keyed: 1, text: "La petreceri vorbesc cu mulți oameni diferiți." },
  { id: 12, factor: "A", keyed: 1, text: "Simt emoțiile celorlalți." },
  { id: 13, factor: "C", keyed: 1, text: "Îmi place ordinea." },
  { id: 14, factor: "N", keyed: 1, text: "Mă supăr ușor." },
  { id: 15, factor: "I", keyed: -1, text: "Îmi e greu să înțeleg ideile abstracte." },
  { id: 16, factor: "E", keyed: -1, text: "Stau mai degrabă în plan secund." },
  { id: 17, factor: "A", keyed: -1, text: "Nu sunt cu adevărat interesat/ă de ceilalți." },
  { id: 18, factor: "C", keyed: -1, text: "Fac lucrurile alandala." },
  { id: 19, factor: "N", keyed: -1, text: "Rareori mă simt abătut/ă." },
  { id: 20, factor: "I", keyed: -1, text: "Nu am o imaginație prea bogată." },
];

/* The IPIP five-point accuracy scale, "Very Inaccurate" to "Very Accurate". */
const OPTIONS = [
  { label: "Deloc adevărat pentru mine", value: 1 },
  { label: "Mai degrabă neadevărat", value: 2 },
  { label: "Nici adevărat, nici fals", value: 3 },
  { label: "Mai degrabă adevărat", value: 4 },
  { label: "Foarte adevărat pentru mine", value: 5 },
];

const FACTORS: FactorCopy[] = [
  {
    key: "E",
    name: "Extraversie",
    original: "Extraversion",
    what: "Cât cauți compania altora, conversația și agitația din jur.",
    low: {
      label: "Rezervat/ă",
      text: "Te încarci mai degrabă în liniște sau cu puțini oameni. Nu înseamnă timiditate și nici că nu îți plac oamenii.",
    },
    high: {
      label: "Sociabil/ă",
      text: "Grupurile și conversația îți dau energie, iar cu oameni noi vorbești ușor.",
    },
  },
  {
    key: "A",
    name: "Agreabilitate",
    original: "Agreeableness",
    what: "Cât ții cont de ce simt și de ce au nevoie ceilalți.",
    low: {
      label: "Detașat/ă",
      text: "Te uiți întâi la fapte și la propriile scopuri, spui mai ușor nu și intri în conflict când e nevoie.",
    },
    high: {
      label: "Empatic/ă",
      text: "Observi repede ce simt ceilalți și îți pasă de problemele lor, uneori înaintea propriilor nevoi.",
    },
  },
  {
    key: "C",
    name: "Conștiinciozitate",
    original: "Conscientiousness",
    what: "Cât de organizat/ă și de consecvent/ă ești cu lucrurile de făcut.",
    low: {
      label: "Flexibil/ă",
      text: "Te descurci cu planurile schimbate din mers, iar lucrurile mărunte se amână sau se pierd mai ușor.",
    },
    high: {
      label: "Organizat/ă",
      text: "Îți place ordinea, termini la timp ce ai de făcut și ții cont de detalii.",
    },
  },
  {
    key: "N",
    name: "Nevrotism",
    original: "Neuroticism",
    what: "Cât de des și de puternic reacționezi emoțional la stres și la neplăceri. E o trăsătură pe care o are fiecare om, în grade diferite, nu o tulburare.",
    low: {
      label: "Calm/ă",
      text: "Emoțiile neplăcute vin mai rar și trec mai repede, iar stresul te clatină mai puțin.",
    },
    high: {
      label: "Reactiv/ă emoțional",
      text: "Simți intens neliniștea, supărarea sau tristețea, iar dispoziția se schimbă mai ușor.",
    },
  },
  {
    key: "I",
    name: "Intelect și imaginație",
    original: "Intellect/Imagination",
    what: "Cât te atrag ideile, imaginația și lucrurile noi. În alte chestionare apare ca „deschidere spre experiență”.",
    low: {
      label: "Practic/ă",
      text: "Preferi concretul și ce funcționează deja în locul teoriilor și al speculațiilor.",
    },
    high: {
      label: "Imaginativ/ă",
      text: "Îți place să te joci cu idei abstracte, îți imaginezi ușor și te atrage ce e nou.",
    },
  },
];

const ALLOWED = OPTIONS.map((o) => o.value);

function scoreMiniIpip(answers: number[]): FactorScores {
  if (answers.length !== QUESTIONS.length) {
    throw new Error("Răspunsuri invalide.");
  }
  const scores: FactorScores = { E: 0, A: 0, C: 0, N: 0, I: 0 };
  QUESTIONS.forEach((q, i) => {
    const a = answers[i];
    if (!ALLOWED.includes(a)) throw new Error("Valori de răspuns invalide.");
    scores[q.factor] += q.keyed === 1 ? a : 6 - a;
  });
  return scores;
}

export const personalitateConfig: ProfileTestConfig = {
  slug: "personalitate",
  questions: QUESTIONS,
  options: OPTIONS,
  allowedAnswerValues: ALLOWED,
  factors: FACTORS,
  factorMin: 4,
  factorMax: 20,
  score: scoreMiniIpip,
  totalQuestions: QUESTIONS.length,
  estimatedMinutes: 3,
  stepperFootnote:
    "Descrie-te așa cum ești de obicei acum, nu cum ți-ai dori să fii. Nu există răspunsuri bune sau rele.",
  source: { name: "Mini-IPIP (International Personality Item Pool)" },
  meta: {
    title: "Test de personalitate Big Five gratuit (Mini-IPIP) | Mindtale",
    description:
      "Test de personalitate gratuit, în română: 20 de afirmații din Mini-IPIP, un chestionar public al modelului Big Five. Primești un profil pe cinci dimensiuni, nu un tip.",
    keywords: [
      "test de personalitate",
      "test personalitate",
      "test de personalitate gratuit",
      "test big five",
      "big five personalitate",
      "mini-ipip",
      "ipip",
      "cele cinci mari trasaturi de personalitate",
      "test personalitate online",
    ],
    canonicalUrl: "https://mindtale.ro/teste/personalitate",
  },
};
